import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { foodCategory } from "utils/common";
import { toast } from "react-toastify";

const AIButton = ({ clientDetailsData, foodPreference, setDietPlan }) => {
  const [open, setOpen] = useState(false);
  const [userNotes, setUserNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [biochemicalNotes, setBiochemicalNotes] = useState("");

  useEffect(() => {
    if (clientDetailsData?.biochemical?.length) {
      const sortedBiochemical = [...clientDetailsData.biochemical].sort(
        (a, b) => (b.timestamp?.seconds ?? 0) - (a.timestamp?.seconds ?? 0)
      );
      const latest = sortedBiochemical[0];

      const summary = Object.entries(latest)
        .filter(([key, val]) => val && typeof val !== "object")
        .map(([key, val]) => `${key}: ${val}`)
        .join(" | ");

      setBiochemicalNotes(summary);
    }
  }, [clientDetailsData]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setAiResponse("");
    setUserNotes("");
  };

  const formattedPrompt = `
Generate a full-day diet plan in the following strict JSON format:

Each meal slot must be a key: earlyMorning, breakfast, midMorning, brunch, lunch, midMeal, evening, dinner, lateDinner, lateNight.

Each key should contain an array of food objects with this structure:
- name (string)
- description (string)
- preparation (string)
- qty (string or number)
- ingredients (array of strings)
- foodCategory (object: { label, value })
  → Must be one of:
${foodCategory.map((item) => `  - ${item.label}`).join("\n")}
- calories (number)
- howToTake (string)
- nutritions (array of objects: { type, grams, unit })
  → Only include if nutrient is one of: ["Protein", "Fat", "Carbohydrate", "Sugar", "Magnesium", "Fibre", "Calcium", "Iron"]
  → Do not include any other types like "Vitamin C", etc.
  → unit must be "g" or "mg"
- foodType (array of strings)
- time (set from user input if available; otherwise use default healthy times)
- id (unique string or UUID)
- unit (object: { label, value }) — must match from:
  "Litre", "Gms", "Ml", "Table Spoon", "Tablet", "Softgel", "Katori", "Tea Cup",
  "Tea Spoon", "Kernel", "Bowl", "Scoop", "Piece", "Nuts", "Servings", "Glass",
  "Small", "Big", "Medium"
- weight (can be empty string)

Use the following biochemical data and lifestyle info:
${biochemicalNotes}
${foodPreference ? `\nClient's food preference (strictly respect this): ${foodPreference}\n` : ""}

User's custom notes:
${userNotes}

Return ONLY valid raw JSON with no \`\`\`, markdown, or explanations.
`;

  const handleGenerate = async () => {
    setLoading(true);

    try {
      const res = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          Authorization:
            "Bearer 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",
          "Content-Type": "application/json",
          "HTTP-Referer": "https://hoppscotch.io",
          "X-Title": "Gemma Test",
        },
        body: JSON.stringify({
          // model: "meta-llama/llama-3.3-70b-instruct:free",
          // model: "qwen/qwen-2.5-72b-instruct:free",
          // model: "mistralai/mistral-nemo:free",
          model: "openrouter/free",
          messages: [{ role: "user", content: formattedPrompt }],
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error: ${res.status}`);
      }

      let data;
      try {
        data = await res.json();
      } catch (e) {
        console.error("AI response was not valid JSON:", e);
        toast.error("Failed to generate diet plan. Retry!");
        return;
      }
      const reply = data?.choices?.[0]?.message?.content || "";

      const match = reply.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
      const rawJson = match ? match[1].trim() : reply.trim();

      try {
        const parsedDietPlan = JSON.parse(rawJson);
        if (!parsedDietPlan || typeof parsedDietPlan !== "object") {
          toast.error("Error parsing AI response. Please try again.");
          return;
        }
        const safePlan = {};
        Object.entries(parsedDietPlan).forEach(([key, val]) => {
          if (Array.isArray(val)) safePlan[key] = val;
        });
        if (Object.keys(safePlan).length === 0) {
          toast.error("Error parsing AI response. Please try again.");
          return;
        }
        setDietPlan(safePlan);
        toast.success("Diet plan generated successfully!");
        setOpen(false);
        setUserNotes("");
      } catch (parseErr) {
        console.error("Failed to parse AI response as JSON:", parseErr);
        toast.error("Error parsing AI response. Please try again.");
      }
    } catch (err) {
      console.error("AI error:", err);
      toast.error("Failed to generate diet plan. Retry!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Generate Diet Plan with AI" arrow>
        <Box
          onClick={handleClickOpen}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            display: "flex",
            alignItems: "center",
            backgroundColor: "#1B2559",
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "999px",
            boxShadow: 3,
            cursor: "pointer",
            zIndex: 1300,
            "&:hover": {
              backgroundColor: "#151C47",
            },
          }}
        >
          <AutoAwesomeIcon sx={{ mr: 1 }} />
          <Typography fontWeight="500" fontSize="14px">
            Generate with AI
          </Typography>
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            backgroundColor: "#F7F9FC",
            color: "#1B2559",
            borderBottom: "1px solid #E0E6ED",
          }}
        >
          Generate Diet Plan with AI
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#F7F9FC" }} dividers>
          <TextField
            placeholder="Add meal timings, recipe ideas, or personal notes"
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              sx: {
                paddingTop: "12px",
                backgroundColor: "#F7F9FC",
              },
              // Ensures the textarea is styled directly
              inputComponent: "textarea",
            }}
            sx={{
              "& .MuiOutlinedInput-input": {
                width: "100% !important",
                height: "57px !important",
                overflow: "hidden",
                padding: "0 !important",
              },
              "& .MuiOutlinedInput-inputMultiline": {
                width: "100% !important",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#8A94A6",
                opacity: 1,
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
          />

          {loading && (
            <Box mt={2} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#F7F9FC" }}>
  <Box display="flex" flexDirection="column" alignItems="flex-end" width="100%">
    {/* Buttons Row */}
    <Box display="flex" alignItems="center" justifyContent={"center"} gap={1}>
      <Button onClick={handleClose} sx={{ color: "#1B2559" }}>
        Close
      </Button>

      <Button
        onClick={handleGenerate}
        variant="contained"
        disabled={loading}
        sx={{
          color: "#fff",
          backgroundColor: "#1B2559",
          textTransform: "none",
          fontWeight: 500,
          "&:hover": {
            backgroundColor: "#151C47",
          },
        }}
      >
        Generate
      </Button>
    </Box>

    {/* Beta label centered under both buttons */}
    <Tooltip
      title="This feature is in Beta and under active development. You may encounter issues."
      arrow
    >
      <Typography
        variant="caption"
        sx={{
          color: "#8A94A6",
          cursor: "help",
          mt: 0.5,
          alignSelf: "center",
        }}
      >
        * In Development
      </Typography>
    </Tooltip>
  </Box>
</DialogActions>
      </Dialog>
    </>
  );
};

export default AIButton;
